import { ReactNode } from 'react'
import { ColumnProps } from 'primereact/column'
import { DataTableProps, SortOrder } from 'primereact/datatable'

import {
    DateRangeFilterOverlay,
    EnumFilterOverlay,
    NumericRangeFilterOverlay,
} from './FilterOverlay'
import TableControl from '../../models/TableControl'
import CustomIcon from '../common/pure/CustomIcon'
import { BasicOption } from '../../tools/settings'

// NOTE: Sorting and filtering features do not use the model of the table.
type AnyTableProps = DataTableProps<any>

interface SortableTableProps
    extends Pick<AnyTableProps, 'onSort' | 'sortField' | 'sortOrder' | 'removableSort'> {}
export const getSortableTableProps = <T,>(table: TableControl<T>): SortableTableProps => ({
    removableSort: true,
    onSort: (e) => table.sort(e.sortField),
    sortField: table.sortBy,
    sortOrder: table.ascendingInt as SortOrder,
})

interface FilterableTableProps extends Pick<AnyTableProps, 'filterIcon'> {}
export const getFilterableTableProps = (): FilterableTableProps => ({
    filterIcon: <CustomIcon name="filter" size={16} />,
})

interface SortableColumnProps extends Pick<ColumnProps, 'sortable' | 'field'> {}
export const getSortableColumnProps = (
    field: string,
    disabled: boolean = false
): SortableColumnProps => ({
    field: field,
    sortable: !disabled,
})

const getFilterableColumnProps = <T,>(
    table: TableControl<T>,
    field: string,
    filterElement: ReactNode
) => ({
    pt: { filterOverlay: { className: 'hidden-filter-overlay' } },
    headerClassName: table.hasFilter(field) ? 'active-filter-header' : '',
    filter: true,
    filterElement,
})

interface FilterableColumnProps
    extends Pick<ColumnProps, 'pt' | 'filter' | 'headerClassName' | 'filterElement'> {}

export const getFilterableEnumColumnProps = <T,>(
    field: string,
    table: TableControl<T>,
    options: BasicOption[],
    singleOnly: boolean = false
): FilterableColumnProps =>
    getFilterableColumnProps(
        table,
        field,
        <EnumFilterOverlay
            options={options.map(({ label, value }) => ({
                label,
                value,
                state: table.hasFilterState(field, value),
            }))}
            isSelected={table.hasFilter(field)}
            onReset={() => table.resetFilter(field)}
            onChange={(value) => {
                if (singleOnly) {
                    if (table.hasFilterState(field, value)) table.resetFilter(field)
                    else table.filter(field, [value])
                } else {
                    let next = table.getFilter<string[]>(field) ?? []
                    if (next.includes(value)) next = next.filter((f) => f !== value)
                    else next.push(value)
                    table.filter(field, next)
                }
            }}
        />
    )

export const getFilterableNumericRangeColumnProps = <T,>(
    field: string,
    table: TableControl<T>
): FilterableColumnProps =>
    getFilterableColumnProps(
        table,
        field,
        <NumericRangeFilterOverlay
            min={table.getFilter<number[]>(field)?.[0]}
            max={table.getFilter<number[]>(field)?.[1]}
            isSelected={table.hasFilter(field)}
            onReset={() => table.resetFilter(field)}
            onChange={(min, max) => table.filter(field, [min, max])}
        />
    )

export const getFilterableDateRangeColumnProps = <T,>(
    field: string,
    table: TableControl<T>
): FilterableColumnProps =>
    getFilterableColumnProps(
        table,
        field,
        <DateRangeFilterOverlay
            range={table.getFilter<Date[]>(field) ?? []}
            isSelected={table.hasFilter(field)}
            onReset={() => table.resetFilter(field)}
            onChange={(dates) => table.filter(field, dates)}
        />
    )
